import * as actions from 'bc/store/actions';
import {composeReducers, reduceArrayItem} from 'common/utils/reducers';
import {isEmpty, isEqual} from 'lodash';
import {getUniqueId} from 'common/utils/guid';
import {isEditRunning} from 'bc/services/dataStreamService';

const bigQueryDataStreamReducer = composeReducers((state, {type, payload}) => {
  const getSelectedIndex = (id = state.selectedItemId) => state.streams.data.findIndex((item) => item.id === id);

  const red = (item, itemPayload) => ({...item, ...itemPayload});

  const redWrapper = (_payload, index = getSelectedIndex()) => ({
    ...state,
    streams: {
      ...state.streams,
      data: reduceArrayItem(red, state.streams.data, index, _payload || payload),
    },
  });

  const getSourceIdsArr = (sourcesIdArr, item) => {
    if (item.sourceColumn) {
      if (!sourcesIdArr.includes(item.sourceColumn)) {
        sourcesIdArr.push(item.sourceColumn);
      }
    } else if (item.transform && item.transform.input) {
      item.transform.input.forEach((a) => {
        getSourceIdsArr(sourcesIdArr, a);
      });
    }
    return sourcesIdArr;
  };

  const getItemName = (item) => {
    if (item.transform && item.transform.input) {
      return getSourceIdsArr([], item)[0].name;
    }
    if (item.sourceColumn) {
      return item.sourceColumn;
    }
    return item.name;
  };

  const getItemFromSchema = (columns, item) =>
    columns.find(
      (a) =>
        a.sourceColumn === item.name ||
        (a.transform && a.transform.input && a.transform.input.length === 1 && item.name === getSourceIdsArr([], a)[0]),
    );

  const getItemMatch = (stream, item) => {
    let itemMatch = {
      ...item,
    };
    if (stream.timestampColumn === item.name) {
      itemMatch.type = 'timestamp';
      itemMatch.isMerged = true;
    } else if (stream.metrics.includes(item.name) || stream.dimensions.includes(item.name)) {
      itemMatch = {
        ...itemMatch,
        ...getItemFromSchema(stream.schema && stream.schema.columns ? stream.schema.columns : [], item),
        isMerged: true,
      };
    } else if (
      stream.uiState &&
      stream.uiState.unAssignedColumns &&
      stream.uiState.unAssignedColumns.includes(item.name)
    ) {
      itemMatch.type = '';
      itemMatch.isMerged = true;
    }
    return itemMatch;
  };

  const stream = state.streams.data[getSelectedIndex()];

  switch (type) {
    case actions.setBigQueryStreamProjectId.TYPE: {
      return redWrapper({
        ...payload,
        defaultDataset: null,
        metrics: [],
        dimensions: [],
        schema: {
          columns: [],
          sourceColumns: [],
        },
        uiState: {},
        timestampColumn: null,
      });
    }
    case actions.setBigQueryStreamDataSet.TYPE: {
      const streamObj = {
        ...payload,
        metrics: [],
        dimensions: [],
        schema: {
          columns: [],
          sourceColumns: [],
        },
        uiState: {},
        timestampColumn: null,
      };

      if (stream && stream.uiState && stream.uiState.id) {
        streamObj.uiState.id = stream.uiState.id;
      }
      return redWrapper(streamObj);
    }
    case actions.setBigQueryStreamQuery.TYPE: {
      let timeItem = {};
      let itemMatch = {};
      let unifiedSchema = [];

      if (stream.uiState && stream.uiState.queryPreviewColumns && !isEditRunning(stream.id)) {
        payload.queryPreviewColumns.forEach((newItem) => {
          itemMatch = {};
          stream.uiState.queryPreviewColumns.forEach((oldItem) => {
            if (isEqual(newItem, oldItem)) {
              itemMatch = getItemMatch(stream, newItem);
            }
          });
          if (isEmpty(itemMatch)) {
            itemMatch = {...newItem};
          }
          unifiedSchema.push({...itemMatch});
        });
      } else if (stream.metrics.length || stream.dimensions.length || !isEmpty(stream.timestampColumn)) {
        payload.queryPreviewColumns.forEach((newItem) => {
          itemMatch = getItemMatch(stream, newItem);
          unifiedSchema.push({...itemMatch});
        });
      } else {
        unifiedSchema = [...payload.queryPreviewColumns];
      }

      // get time item
      if (stream.timestampColumn) {
        timeItem = unifiedSchema.find((item) => item.type === 'timestamp' && item.name === stream.timestampColumn);
      }
      if (isEmpty(timeItem)) {
        timeItem = unifiedSchema.find((item) => item.type === 'timestamp');
      }
      if (!timeItem) {
        timeItem = {};
      }

      // extract the updated unifiedSchema
      const propsToUpdate = {
        query: payload.query,
        useLegacySQL: payload.useLegacySQL,
        uiState: {
          ...stream.uiState,
          queryPreviewColumns: payload.queryPreviewColumns,
          unAssignedColumns: unifiedSchema
            .filter((item) => item.type !== 'metric' && item.type !== 'dimension' && item.name !== timeItem.name)
            .map((item) => item.name),
        },
        dimensions: unifiedSchema.filter((item) => item.type === 'dimension').map((item) => getItemName(item)),
        metrics: unifiedSchema.filter((item) => item.type === 'metric').map((item) => getItemName(item)),
        timestampColumn: timeItem.name || '',
        schema: {
          columns: [],
          sourceColumns: [],
        },
      };

      // schema population
      let unifiedItem = {};
      propsToUpdate.metrics.forEach((itemName) => {
        const metric = unifiedSchema.find((a) => getItemName(a) === itemName);
        unifiedItem = {
          id: metric.id || getUniqueId(),
          name: metric.name,
          type: 'metric',
        };
        if (metric.sourceColumn) {
          unifiedItem.sourceColumn = metric.sourceColumn;
        }
        if (metric.transform) {
          unifiedItem.transform = {...metric.transform};
        }
        if (!metric.sourceColumn && !metric.transform) {
          unifiedItem.sourceColumn = metric.name;
        }
        if (metric.targetType) {
          unifiedItem.targetType = metric.targetType;
        }
        if (metric.hidden !== undefined) {
          unifiedItem.hidden = metric.hidden;
        }
        const originalName = unifiedItem.sourceColumn || getSourceIdsArr([], unifiedItem)[0];
        propsToUpdate.schema.columns.push(unifiedItem);
        propsToUpdate.schema.sourceColumns.push({
          id: originalName,
          name: originalName,
        });
      });
      propsToUpdate.dimensions.forEach((itemName) => {
        const dimension = unifiedSchema.find((a) => getItemName(a) === itemName);
        unifiedItem = {
          id: dimension.id || getUniqueId(),
          name: dimension.name,
          type: 'dimension',
        };
        if (dimension.sourceColumn) {
          unifiedItem.sourceColumn = dimension.sourceColumn;
        }
        if (dimension.transform) {
          unifiedItem.transform = {...dimension.transform};
        }
        if (!dimension.sourceColumn && !dimension.transform) {
          unifiedItem.sourceColumn = dimension.name;
        }
        if (dimension.hidden !== undefined) {
          unifiedItem.hidden = dimension.hidden;
        }
        const originalName = unifiedItem.sourceColumn || getSourceIdsArr([], unifiedItem)[0];
        propsToUpdate.schema.columns.push(unifiedItem);
        propsToUpdate.schema.sourceColumns.push({
          id: originalName,
          name: originalName,
        });
      });

      // get a list of all the current indexes
      const existingIndexArr = [...propsToUpdate.dimensions, ...propsToUpdate.metrics];

      // compare and copy the user created columns from the current schema
      if (stream.schema && stream.schema.columns) {
        stream.schema.columns.forEach((item) => {
          if (
            item.sourceColumn !== undefined ||
            (item.transform &&
              item.transform.input &&
              item.transform.input.length === 1 &&
              getSourceIdsArr([], item).length === 1)
          ) {
            const sourceColumn = item.sourceColumn || getSourceIdsArr([], item)[0];
            if (
              existingIndexArr.includes(parseInt(sourceColumn, 10)) &&
              getItemFromSchema(unifiedSchema, sourceColumn, item.name)
            ) {
              // user added columns that are a copy of existing column
              propsToUpdate.schema.columns.push({...item});
            }
          } else if (item.transform && item.transform.name === 'const') {
            // user added const column
            propsToUpdate.schema.columns.push({...item});
          } else if (item.transform && item.transform.input && item.transform.input.length > 1) {
            // user added concat columns
            let shouldPush = true;
            getSourceIdsArr([], item).forEach((id) => {
              if (!existingIndexArr.includes(id) || getItemFromSchema(unifiedSchema, id, item.name) === undefined) {
                shouldPush = false;
              }
            });
            if (shouldPush) {
              propsToUpdate.schema.columns.push({...item});
            }
          }
        });
      }
      return {...redWrapper(propsToUpdate), isStreamNeedUpdating: true};
    }

    case actions.setBigQueryStreamClearAllDiametrics.TYPE: {
      const meta = stream.uiState.queryPreviewColumns;
      const mod = {
        metrics: [],
        dimensions: [],
        timestampColumn: null,
        uiState: {
          ...stream.uiState,
          unAssignedColumns: meta.map((a) => a.name),
        },
      };
      return redWrapper(mod);
    }

    case actions.removeBigQueryStreamDiametrics.TYPE: {
      let updateSchema = true;
      const mod = {};
      if (stream.metrics.indexOf(payload) >= 0) {
        updateSchema = true;
        mod.metrics = stream.metrics.filter((a) => a !== payload);
      } else if (stream.dimensions.indexOf(payload) >= 0) {
        updateSchema = true;
        mod.dimensions = stream.dimensions.filter((a) => a !== payload);
      } else if (stream.timestampColumn === payload) {
        updateSchema = true;
        mod.timestampColumn = null;
      }
      mod.uiState = {
        ...stream.uiState,
        unAssignedColumns: [...stream.uiState.unAssignedColumns, payload],
      };
      if (updateSchema) {
        mod.schema = {
          columns: stream.schema.columns.filter((a) => {
            if (a.sourceColumn) {
              return a.sourceColumn !== payload;
            }
            return !getSourceIdsArr([], a).includes(payload);
          }),
          sourceColumns: stream.schema.sourceColumns.filter((a) => a.name !== payload),
        };
      }
      return redWrapper(mod);
    }

    case actions.setBigQueryStreamDiametricsChange.TYPE: {
      const streamModifications = {
        uiState: {...stream.uiState},
      };
      let allowed = true;

      const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
      };

      if (payload.source.droppableId === payload.destination.droppableId) {
        switch (payload.destination.droppableId) {
          case 'dmAllColumns': {
            streamModifications.uiState.unAssignedColumns = reorder(
              stream.uiState.unAssignedColumns,
              payload.source.index,
              payload.destination.index,
            );
            break;
          }
          case 'dmMetrics': {
            streamModifications.metrics = reorder(stream.metrics, payload.source.index, payload.destination.index);
            break;
          }
          case 'dmDimensions': {
            streamModifications.dimensions = reorder(
              stream.dimensions,
              payload.source.index,
              payload.destination.index,
            );
            break;
          }
          case 'dmDate': {
            break;
          }
          default:
            return state;
        }
      } else {
        const {queryPreviewColumns} = stream.uiState;
        switch (payload.destination.droppableId) {
          case 'dmAllColumns': {
            streamModifications.uiState.unAssignedColumns = [...stream.uiState.unAssignedColumns];
            streamModifications.uiState.unAssignedColumns.splice(payload.destination.index, 0, payload.draggableId);
            break;
          }
          case 'dmMetrics': {
            streamModifications.metrics = [...stream.metrics];
            streamModifications.metrics.splice(payload.destination.index, 0, payload.draggableId);
            break;
          }
          case 'dmDimensions': {
            streamModifications.dimensions = [...stream.dimensions];
            streamModifications.dimensions.splice(payload.destination.index, 0, payload.draggableId);
            break;
          }
          case 'dmDate': {
            const item = queryPreviewColumns.find((a) => a.name === payload.draggableId);
            if (item.type === 'timestamp') {
              if (stream.timestampColumn) {
                streamModifications.uiState.unAssignedColumns = [...stream.uiState.unAssignedColumns];
                streamModifications.uiState.unAssignedColumns.splice(0, 0, stream.timestampColumn);
              }
              streamModifications.timestampColumn = item.name;
            } else {
              allowed = false;
            }
            break;
          }
          default:
            return state;
        }

        if (allowed) {
          switch (payload.source.droppableId) {
            case 'dmAllColumns': {
              streamModifications.uiState.unAssignedColumns = [
                ...(streamModifications.uiState.unAssignedColumns || stream.uiState.unAssignedColumns),
              ];
              streamModifications.uiState.unAssignedColumns.splice(
                streamModifications.uiState.unAssignedColumns.findIndex((a) => a === payload.draggableId),
                1,
              );
              break;
            }
            case 'dmMetrics': {
              streamModifications.metrics = [...stream.metrics];
              streamModifications.metrics.splice(
                streamModifications.metrics.findIndex((a) => a === payload.draggableId),
                1,
              );
              break;
            }
            case 'dmDimensions': {
              streamModifications.dimensions = [...stream.dimensions];
              streamModifications.dimensions.splice(
                streamModifications.dimensions.findIndex((a) => a === payload.draggableId),
                1,
              );
              break;
            }
            case 'dmDate': {
              streamModifications.timestampColumn = null;
              break;
            }
            default:
              return state;
          }
        }
      }
      return redWrapper(streamModifications);
    }

    default:
      return state;
  }
});

export default bigQueryDataStreamReducer;
